/** Which of our two German voices should read this line.
 *
 * Lesson dialogues name their speakers — "Anna", "Herr Meier" — and until
 * now every line was read by the browser's own synthesiser in one voice.
 * The client heard that as "a computer voice", and asked for the male and
 * female parts to be spoken by the male and female voices we built.
 *
 * The rules, most reliable first:
 *
 *   1. A German title says it outright. Herr is a man, Frau is a woman,
 *      and our exam content uses them constantly.
 *   2. Otherwise the first name, from the set our lesson content actually
 *      uses. Deliberately a short list of the names in the course rather
 *      than an attempt at every German name — a wrong guess is worse than
 *      no guess, and rule 3 already produces a sensible answer.
 *   3. Otherwise alternate by the order the speakers appear in the scene.
 *      We may not know who is a man, but we always know that two different
 *      speakers should not sound identical, which is the actual point.
 */
export type SpeakerVoice = "male" | "female";

const FEMALE_NAMES = new Set([
  "anna", "maria", "lisa", "julia", "sofia", "sophie", "emma", "laura",
  "sarah", "nina", "petra", "sabine", "katrin", "monika", "eva", "lena",
  "hanna", "clara", "marie", "elena", "ayse", "fatma", "ana",
]);

const MALE_NAMES = new Set([
  "paul", "peter", "thomas", "michael", "max", "jonas", "stefan", "klaus",
  "martin", "andreas", "felix", "lukas", "david", "hans", "jan", "tim",
  "ahmet", "mehmet", "ali", "marco", "bruno",
]);

/** The speaker's given name, lower-cased, with any title removed. */
function firstName(speaker: string): string {
  const cleaned = speaker.trim().replace(/[:：]\s*$/, "");
  const words = cleaned.split(/\s+/).filter(Boolean);
  const withoutTitle = words.filter(
    (w) => !/^(herr|frau|dr\.?|prof\.?)$/i.test(w),
  );
  return (withoutTitle[0] ?? "").toLowerCase();
}

/** The voice each speaker in one scene should use.
 *
 * Resolved for the WHOLE scene rather than per line, because the rules
 * interact: naming one speaker constrains the other. "Anna" is known to be
 * female and "Tom" is not in our list, so deciding Tom on his own gives
 * whatever the positional rule says — which was female, making a two-person
 * dialogue sound like one person again. Knowing Anna is female is precisely
 * what tells us Tom should not be.
 */
export function voicesForScene(
  scene: readonly (string | undefined)[],
): Map<string, SpeakerVoice> {
  const distinct: string[] = [];
  for (const s of scene) {
    const key = (s ?? "").trim();
    if (key && !distinct.includes(key)) distinct.push(key);
  }

  const voices = new Map<string, SpeakerVoice>();
  const unknown: string[] = [];
  for (const name of distinct) {
    const known = knownVoice(name);
    if (known) voices.set(name, known);
    else unknown.push(name);
  }

  // Give each unknown speaker the voice that is doing the least work, so
  // they land opposite whoever we DO know about.
  const count = (v: SpeakerVoice) =>
    [...voices.values()].filter((x) => x === v).length;
  for (const name of unknown) {
    const male = count("male");
    const female = count("female");
    voices.set(name, male <= female ? "male" : "female");
  }
  return voices;
}

/** male/female where the text says so, null where it does not.
 *
 * The title is compared as a WORD rather than by prefix: "Frauke" starts
 * with "frau" and is a woman's name by accident, not by title, and a
 * prefix test would be right there for the wrong reason. */
function knownVoice(speaker: string): SpeakerVoice | null {
  const words = speaker.trim().split(/\s+/).filter(Boolean);
  const title = (words[0] ?? "").toLowerCase().replace(/[:.]$/, "");
  if (title === "herr") return "male";
  if (title === "frau") return "female";

  const name = firstName(speaker);
  if (MALE_NAMES.has(name)) return "male";
  if (FEMALE_NAMES.has(name)) return "female";
  return null;
}

/** Decide a voice for `speaker`, given every speaker in the same scene so
 *  unknown names can at least be told apart from the ones we know. */
export function voiceForSpeaker(
  speaker: string | undefined,
  scene: readonly (string | undefined)[] = [],
): SpeakerVoice {
  if (!speaker) return "female";
  const direct = knownVoice(speaker);
  if (direct) return direct;
  const resolved = voicesForScene(scene.length ? scene : [speaker]);
  return resolved.get(speaker.trim()) ?? "male";
}
